import {
  buildCommunityBoardId,
  boardSubredditTitle,
  COMMUNITY_BOARD_HEADER_PREFIX,
  COMMUNITY_BOARD_WORLD_TITLE_SLUG,
  communityBoardScopeTitleSlug,
  cityLabelFromBoardId,
  isGlobalBoard,
} from '@/lib/communityBoards'
import {
  expandLocationTokensToLaunchedCities,
  getMultiLocationFilterDisplay,
  isEffectivelyAllLocations,
  type AccordionRegion,
} from '@/lib/locationRegions'

export function resolveFeedCityLabelsFromLocationSelection(params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
}): readonly string[] {
  if (
    isEffectivelyAllLocations(
      [...params.selectedLocations],
      params.allLocationsText,
      params.launchedCities,
    )
  ) {
    return params.launchedCities
  }

  return expandLocationTokensToLaunchedCities(
    [...params.selectedLocations],
    params.allLocationsText,
    new Set(params.launchedCities),
  )
}

/** Public city board ids included in the current location bucket (global = all launched cities). */
export function resolveFeedBoardIdsFromLocationSelection(params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
}): readonly string[] {
  return resolveFeedCityLabelsFromLocationSelection(params).map((cityLabel) =>
    buildCommunityBoardId('everyone', { kind: 'city', cityLabel }),
  )
}

/** Posting stays city-specific — only when the feed scope is exactly one city. */
export function resolveComposeCityBoardIdFromLocationSelection(params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
}): string | null {
  const feedCityLabels = resolveFeedCityLabelsFromLocationSelection(params)
  if (feedCityLabels.length !== 1) return null

  return buildCommunityBoardId('everyone', { kind: 'city', cityLabel: feedCityLabels[0]! })
}

export function boardFeedPageTitle(params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
  readonly locationAccordionRegions?: readonly AccordionRegion[]
}): string {
  if (
    isEffectivelyAllLocations(
      [...params.selectedLocations],
      params.allLocationsText,
      params.launchedCities,
    )
  ) {
    return `${COMMUNITY_BOARD_HEADER_PREFIX}/${COMMUNITY_BOARD_WORLD_TITLE_SLUG}`
  }

  const feedCityLabels = resolveFeedCityLabelsFromLocationSelection(params)
  if (feedCityLabels.length === 1) {
    return boardSubredditTitle(
      buildCommunityBoardId('everyone', { kind: 'city', cityLabel: feedCityLabels[0]! }),
    )
  }

  const scopeLabel = getMultiLocationFilterDisplay(
    [...params.selectedLocations],
    params.allLocationsText,
    params.locationAccordionRegions,
  )
  const scopeSlug = scopeLabel
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '')
    .slice(0, 40)

  const scopeTitleSlug = communityBoardScopeTitleSlug(scopeSlug)
  return `${COMMUNITY_BOARD_HEADER_PREFIX}/${scopeTitleSlug}`
}

/** @deprecated Prefer resolveComposeCityBoardIdFromLocationSelection for posting. */
export function resolveCityBoardIdFromLocationSelection(params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
}): string | null {
  return resolveComposeCityBoardIdFromLocationSelection(params)
}

/** @deprecated Prefer resolveFeedBoardIdsFromLocationSelection for feeds. */
export function resolveBoardIdFromLocationSelection(params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
}): string {
  const composeBoardId = resolveComposeCityBoardIdFromLocationSelection(params)
  if (composeBoardId) return composeBoardId

  return buildCommunityBoardId('everyone', { kind: 'global' })
}

/** @deprecated Global/all-location feeds now show posts from every launched city. */
export function isWorldBoardLandingView(_params: {
  readonly selectedLocations: readonly string[]
  readonly allLocationsText: string
  readonly launchedCities: readonly string[]
}): boolean {
  return false
}

export function boardCityDisplayLabelFromBoardId(boardId: string): string | null {
  return cityLabelFromBoardId(boardId)
}

export function isPostableCommunityBoardId(boardId: string): boolean {
  return !isGlobalBoard(boardId) && cityLabelFromBoardId(boardId) != null
}
